import React, { CSSProperties, createContext, useContext } from 'react';
import { useSortable } from '@dnd-kit/sortable'
import { flexRender, Row } from '@tanstack/react-table';
import { CSS } from '@dnd-kit/utilities'
import useGetColors from 'shared/hooks/useGetColors';
import { Td, Tr } from '@chakra-ui/react';

const DragHandleContext = createContext<{
  listeners?: any;
  attributes?: any;
} | null>(null);

export const useDragHandle = () => {
  const context = useContext(DragHandleContext);
  return context || { listeners: {}, attributes: {} };
};

export type DefaultTableData = {
  id: string | number;
} & Record<string, unknown>;

interface Props<TData extends DefaultTableData> {
  row: Row<TData>;
  draggable: boolean;
  selected?: boolean;
}

const DraggableRow = <TData extends DefaultTableData>({
  row,
  draggable,
  selected,
}: Props<TData>) => {
  const { gray50, blue800 } = useGetColors();
  const {
    transform,
    transition,
    setNodeRef,
    isDragging,
    listeners,
    attributes,
  } = useSortable({
    id: row.original.id,
  });

  const style: CSSProperties = {
    backgroundColor: selected ? "#F3EEFF" : "inherit",
    ...(draggable && {
      transform: CSS.Transform.toString(transform),
      transition,
      opacity: isDragging ? 0.6 : 1,
    }),
  };

  return (
    <DragHandleContext.Provider value={{ listeners, attributes }}>
      <Tr
        key={row.id}
        color={blue800}
        fontWeight="500"
        borderBottom="none"
        sx={{ ...style, "&:hover": { bg: gray50 } }}
        ref={draggable ? setNodeRef : undefined}
      >
        {row.getVisibleCells().map(cell => (
          <Td
            key={cell.id}
            {...cell.column.columnDef?.meta?.styles?.sizes}
            isNumeric={cell.column.columnDef?.meta?.isNumeric}
            py={cell.column.columnDef.id === 'actions' ? '0' : "6px"}
            px={cell.column.columnDef.id === 'actions' ? '0' : "5px"}
          >
            {flexRender(cell.column.columnDef.cell, cell.getContext())}
          </Td>
        ))}
      </Tr>
    </DragHandleContext.Provider>
  );
};

export default DraggableRow;
